import { Injectable, Output, EventEmitter } from '@angular/core';
import { OrderService } from './order.service';
import { RestaurantService } from './restaurant.service';
import { AuthenticationService } from './authentication.service';
import { TranslatePipe } from '@ngx-translate/core';
import { KeenTrackingService } from 'src/app/core/services/keenTracking.service';
import { OrderLevelSelection } from 'src/app/pages/shopping-cart/order-level-extras/order-level-extras.types';
import { OrderFlowTraceService } from './telemetry/order-flow-trace.service';
import { OpenPanelService } from './openPanel.service';
import { CartItemEditService } from './cart-item-edit.service';

@Injectable()
export class ShoppingCartService {
    @Output() cartProductsChanged = new EventEmitter();

    public products: any[] = [];
    public userPoints: number
    public userIsLogged: boolean = false;
    public orderLevelSelections: OrderLevelSelection[] = [];

    constructor(
        private orderService: OrderService,
        private authService: AuthenticationService,
        private restaurantService: RestaurantService,
        private translatePipe: TranslatePipe,
        private keen: KeenTrackingService,
        private orderFlowTrace: OrderFlowTraceService,
        private openPanel: OpenPanelService,
        private cartItemEdit: CartItemEditService,
    ) {}

    useLocalStorageCart() {
        const cart = JSON.parse(localStorage.getItem(`cart-${this.restaurantService.restaurant.info.id}`));
        if (!cart || cart.length)
            return;

        this.products = [...this.products, ...cart.items];
        this.cartProductsChanged.emit(this.products);
        return cart;
    }

    addProductToCart(product) {
        const editingIndex = this.cartItemEdit.consume(this.products, product);
        if (editingIndex !== null) return this.replaceProductAt(editingIndex, product);

		this.keen.event('addProductToCart', product);
        this.products.push(product);
        this.sendTrackingAnalyticsEvents()
        this.cartProductsChanged.emit(this.products);

        // O item entrando no carrinho é onde o funil ganha dinheiro. `in` é o
        // produto (redigido); `out` é o efeito no carrinho — os dois no mesmo
        // evento, para o golden ler uma linha e ter o caso completo.
        this.orderFlowTrace.emit('ClientProductAddedToCart', {
            in: {
                product_id: product && product.id,
                type: product && product.type,
                qty: product && product.qty,
                total_price: product && product.price,
            },
            out: { cart_items: this.products.length, cart_total: this.total() },
        }, 'shoppingCart.addProductToCart');
    }

    private replaceProductAt(itemIndex, product) {
        const previous = this.products[itemIndex];

        this.keen.event('editProductInCart', product);
        this.products[itemIndex] = product;
        this.cartProductsChanged.emit(this.products);

        this.orderFlowTrace.emit('ClientCartItemEdited', {
            in: {
                index: itemIndex,
                product_id: previous && previous.id,
                type: previous && previous.type,
                qty_before: previous && previous.qty,
            },
            out: {
                qty_after: product && product.qty,
                item_subtotal: this.itemSubtotal(itemIndex),
                cart_total: this.total(),
            },
        }, 'shoppingCart.replaceProductAt');
    }

    sendTrackingAnalyticsEvents() {
        let totalCart = this.total()

        let cartProducts = this.products.map((product, index) => {
            let priceProduct = product.totalPrice ? product.totalPrice : this.generalPrice(index);
            let totalPriceProduct = this.itemSubtotal(index);

            return {
                id: product.id,
                name: product.name,
                price: priceProduct,
                quantity: product.qty,
                totalPrice: totalPriceProduct
            }
        })

        this.openPanel.track('add_to_cart', {
            value: totalCart,
            currency: this.restaurantService.restaurant.info.currency,
            // Soma de qty, não cartProducts.length: o "+" do carrinho incrementa qty na
            // MESMA linha (sem criar linha nova), então .length ficava parado e o evento
            // reportava a mesma contagem de itens de antes do incremento.
            items_count: this.totalQty(),
            items: this.cartItemsCompact(),
        });

        // Evento de conversão do Google Analytics
        gtag('event', 'add_to_cart',{
            'currencyCode':  this.restaurantService.restaurant.info.currency,
            'products': cartProducts,
            'totalCart': totalCart,
            'value' : totalCart,
            'currency':  this.restaurantService.restaurant.info.currency,
        })

        // Evento de conversão do Google Tag Manager
        window.dataLayer.push({
            event: 'add_to_cart',
            currency: this.restaurantService.restaurant.info.currency,
            products: cartProducts,
            totalCart: totalCart,
            value: totalCart
        });


        fbq('track', 'AddToCart',{
            'currencyCode':  this.restaurantService.restaurant.info.currency,
            'products': cartProducts,
            'totalCart': totalCart,
            'value' : totalCart,
            'currency':  this.restaurantService.restaurant.info.currency,
        })
    }

    removeProductByID(productID) {
		this.keen.event('removeProductByID', productID);

        this.products = this.products.filter((item) => {
            return item.id != productID
        });
        this.cartProductsChanged.emit(this.products);

        this.orderService.order.orders = this.products;
        this.sendRemoveFromCartEvent();
    }

    removeProductByIndex(itemIndex) {
		this.keen.event('removeProductByIndex', itemIndex);
        this.products.splice(itemIndex, 1);
        this.cartProductsChanged.emit(this.products);
        this.sendRemoveFromCartEvent();
    }

    // Remoção/decremento re-emitem o ESTADO do carrinho. Sem isso o items_count do
    // funil ficava inflado (só a adição re-emitia) e a composição do carrinho no
    // abandono era irreconstituível — foi o que adiou a peça "itens abandonados" no
    // discovery OP-1137 (P9). O evento carrega o carrinho PÓS-remoção, como o add.
    sendRemoveFromCartEvent() {
        this.openPanel.track('remove_from_cart', {
            value: this.total(),
            currency: this.restaurantService.restaurant.info.currency,
            items_count: this.totalQty(),
            items: this.cartItemsCompact(),
        });
    }

    // Itens do carrinho numa string compacta "id:qty,id:qty" para os rankings por
    // item/categoria (OP-1179) e o futuro "itens abandonados". Só id+qty de propósito:
    // nome/categoria/preço resolvem no MySQL pelo id, e cada byte aqui multiplica por
    // ~360k eventos de carrinho por dia.
    // ⚠️ NÃO usar JSON aqui: o ingest do OpenPanel PARSEIA strings JSON e achata o
    // resultado em chaves pontilhadas (items.0.i, items.0.q...) — validado em produção
    // em 24/09/2026. O formato id:qty não é JSON válido e atravessa intacto como string,
    // que é o que a MV events_funnel_v2 (properties['items']) espera.
    cartItemsCompact(): string {
        return this.products.map((p) => `${p.id}:${p.qty ?? 1}`).join(',');
    }

    prizeSubTotal(itemIndex): string {
        let product = this.products[itemIndex];
        return this.translatePipe.transform('Points:') + ` ${product.points_needed}`;

    }

    generalPrice(itemIndex): number {
        let product = this.products[itemIndex];
        let total = product.price ? product.price : 0;

        if(!('extras' in product))
            return total;

        product.extras.forEach(extra => {
            total += extra.price
        });

        return total
    }

    itemSubtotal(itemIndex): number {
        let product = this.products[itemIndex];
		if (!product)
			return;

        let total = product.price ? product.price : 0;

        switch (product.type) {
            case 'general':
                product.extras.forEach(extra => {
                    total += extra.price * extra.qty
                });
                break;
            case 'pizza':
                total = product.totalPrice;
                if(product.extras) {
                    product.extras.forEach(extra => {
                        total += extra.price * extra.qty
                    });
                }
                break;
            default:
                total = product.totalPrice;
        }

        return total * product.qty; 
    }


    // Limpeza INTENCIONAL do carrinho (botão LIMPAR): emite um remove_from_cart POR
    // LINHA, com o estado do carrinho após cada remoção — mesma semântica da remoção
    // unitária, e é o que registra "o que saiu" também quando o cliente esvazia de uma
    // vez (OP-1137). NÃO usar nos clears pós-envio (payment/mesa) nem na remontagem do
    // repetir-pedido: lá o carrinho esvazia porque o pedido FOI feito ou será recriado —
    // remove_from_cart ali viraria ruído de abandono em cima de uma compra.
    clearCartTracked(needReturnUserPoints: boolean = true) {
        while (this.products.length) {
            this.removeProductByIndex(0);
        }
        this.clearCart(needReturnUserPoints);
    }

    clearCart(needReturnUserPoints: boolean = true) {
		this.keen.event('clearCart');

        this.products = []
        this.orderLevelSelections = [];
        this.cartProductsChanged.emit(this.products);
    }

    setUser() {
        this.userIsLogged= this.authService.isLoggedIn();

        if(this.userIsLogged) {
            if(this.authService.user) {
                this.userPoints =  this.authService.user.points;
            } else {
                this.authService.getUser().then(response => {
                    this.userPoints =  this.authService.user.points;
                    this.keen.setEventBase();
                }).catch(() => {}) // soft-fail: sem user (token expirado), pontos ficam como estão
            }
        }

    }

    setOrderLevelSelection(optionID: number, optionName: string, optionPrice: number, qty: number) {
        const existing = this.orderLevelSelections.findIndex(s => s.id === optionID);
        if (qty <= 0) {
            if (existing !== -1) this.orderLevelSelections.splice(existing, 1);
            return;
        }
        if (existing !== -1) {
            this.orderLevelSelections[existing].qty = qty;
        } else {
            this.orderLevelSelections.push({ id: optionID, name: optionName, price: optionPrice, qty });
        }
    }

    clearOrderLevelSelections() {
        this.orderLevelSelections = [];
    }

    orderLevelExtrasTotal(): number {
        return (this.orderLevelSelections ?? []).reduce((sum, s) => sum + (s.price ?? 0) * (s.qty ?? 0), 0);
    }

    total(): number {
        let total = 0;

        this.products.forEach((item, index) => {
            if(!item.points_needed) {
                total += this.itemSubtotal(index);
            }
        });

        total += this.orderLevelExtrasTotal();

        if (total < 0) {
            total = 0;
        }

        return total;
    }

    // Soma das unidades de todas as linhas do carrinho (item.qty) — não confundir com
    // products.length, que é o nº de LINHAS (cada linha pode ter qty > 1).
    totalQty(): number {
        return this.products.reduce((sum, item) => sum + (item.qty ?? 0), 0);
    }

	countTotalFlavorsQuantitySelected(pizzaProduct) {
		return pizzaProduct.selectedFlavors.reduce((total, flavor) => {
			return total + flavor.quantity;
		}, 0);
	}

	reduceFraction(fraction: number[]): number[] {
		for (var i = fraction[0]; i > 0; i--) {
			if (0 != fraction[0] % i && 0 != fraction[1] % i) continue;
            return [fraction[0], fraction[1]];
		}
	}

    hasItems() {
        return this.products.length > 0;
    }

    /**
     * Avisa que o conteúdo do carrinho mudou sem entrar nem sair item (mudança de
     * quantidade). O `saveLocalStorage` NÃO emite de propósito: ele também roda no
     * ionViewWillEnter, e emitir ali dispararia um refresh a mais em toda entrada de
     * carrinho, em cima do que o próprio ngOnInit dos filhos já faz.
     */
    notifyProductsChanged() {
        this.cartProductsChanged.emit(this.products);
    }

    saveLocalStorage(products = this.products) {
        const cartName = `cart-${this.restaurantService.restaurant.info.id}`;

        const currentCart = JSON.parse(localStorage.getItem(cartName)) || {};
        currentCart.items = products;

        localStorage.setItem(cartName, JSON.stringify(currentCart));
    }
}